/**
 * Login page — two ways to sign in with a passkey:
 *
 *  1. Conditional UI (autofill): a background WebAuthn ceremony is armed on
 *     page load so saved passkeys appear in the username field's autocomplete
 *     dropdown (requires autocomplete="username webauthn" on the input).
 *     Picking one resolves the ceremony → verify → session.
 *  2. Explicit "Sign in with a passkey" button.
 *
 * Challenge hygiene: the server's challenges are single-use and expire after
 * 5 minutes (anti-replay best practice). @simplewebauthn/browser doesn't
 * support aborting a pending conditional ceremony, so instead of a refresh
 * timer we re-arm after failures: if the user idled past the TTL and then
 * picked a passkey, verify fails with "expired" and we silently re-arm with
 * a fresh challenge — the user just picks the passkey again.
 */
import {
  startAuthentication,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
} from '@simplewebauthn/browser';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { postJson, setStatus } from './webauthn.js';

const btn = document.getElementById('passkey-btn') as HTMLButtonElement | null;
const status = document.getElementById('passkey-status');
const autofillHint = document.getElementById('autofill-hint');

let buttonCeremonyActive = false;
let rearmBudget = 5; // guard against silent re-arm loops without user interaction

function getOptions(): Promise<PublicKeyCredentialRequestOptionsJSON> {
  return postJson<PublicKeyCredentialRequestOptionsJSON>('/webauthn/authenticate/options');
}

async function verifyAndRedirect(assertion: AuthenticationResponseJSON): Promise<void> {
  const result = await postJson<{ verified: boolean }>('/webauthn/authenticate/verify', {
    response: assertion,
  });
  if (result.verified) {
    setStatus(status, '✅ Signed in! Redirecting…', 'success');
    setTimeout(() => {
      window.location.href = '/';
    }, 400);
  }
}

/* ------------------------- explicit button flow ------------------------- */

if (btn) {
  if (!browserSupportsWebAuthn()) {
    btn.hidden = false;
    btn.disabled = true;
    setStatus(status, 'This browser does not support WebAuthn passkeys.', 'error');
  } else {
    btn.hidden = false;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      buttonCeremonyActive = true;
      // Opening the modal ceremony cancels any pending conditional one
      // (browser-managed), so the button's fresh challenge is authoritative.

      setStatus(status, 'Preparing passkey sign-in…');
      try {
        const options = await getOptions();
        setStatus(status, 'Waiting for your authenticator…');
        const assertion = await startAuthentication({ optionsJSON: options });
        await verifyAndRedirect(assertion);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Passkey sign-in failed.';
        setStatus(status, `❌ ${message}`, 'error');
        btn.disabled = false;
      } finally {
        buttonCeremonyActive = false;
        rearmBudget = 5;
        void armConditionalUI(); // re-arm autofill for another attempt
      }
    });
  }
}

/* -------------------- conditional UI (autofill) flow -------------------- */

async function armConditionalUI(): Promise<void> {
  if (!(await browserSupportsWebAuthnAutofill())) return;
  if (autofillHint) autofillHint.hidden = false;

  try {
    const options = await getOptions();

    // Resolves ONLY when the user selects a passkey from the username
    // field's autocomplete dropdown.
    const assertion = await startAuthentication({
      optionsJSON: options,
      useBrowserAutofill: true,
    });

    await verifyAndRedirect(assertion);
  } catch (err) {
    // The button flow now owns the session challenge — don't fight it.
    if (buttonCeremonyActive) return;

    const name = (err as Error).name;
    if (name === 'AbortError' || name === 'NotAllowedError') {
      // Ceremony cancelled without user intent (browser took over, etc.)
      if (rearmBudget-- > 0) void armConditionalUI();
      return;
    }

    // Real failure — often "Challenge missing or expired" after idling.
    // Surface it and re-arm with a fresh challenge so the user can just
    // pick the passkey again.
    const message = err instanceof Error ? err.message : 'Passkey autofill failed.';
    setStatus(status, `❌ ${message} — pick your passkey again.`, 'error');
    if (rearmBudget-- > 0) void armConditionalUI();
  }
}

void armConditionalUI();
